/**
 * BIM Architectural Elements
 * 
 * BIM-style parametric BIM components:
 * - Door (with frame, swing direction)
 * - Window (with frame, mullions)
 * - Floor/Slab (from boundary)
 * - Stairs (parametric with treads/risers)
 * - Column (structural)
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useThree, ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import { useAppStore } from '../../state/store';
import { generateId } from '../../utils/helpers';
import { GROUND_PLANE, snapToGroundGrid } from '../../core/scene';
import { applyOsnap } from '../snapping';
import { BACKFACE_DEFINES } from '../../rendering/backfaceTint';

// ============================================================================
// DOOR TOOL
// ============================================================================

export interface DoorData {
    position: number[];
    width: number;
    height: number;
    thickness: number;
    swingAngle: number;       // Current swing (0 = closed, 90 = open)
    swingDirection: 'left' | 'right';
    openDirection: 'inward' | 'outward';
    frameWidth: number;
    material: string;
}

/**
 * Door Tool Hook
 * 
 * Click on wall to place door
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useDoorTool() {
    const [previewPosition, setPreviewPosition] = useState<THREE.Vector3 | null>(null);
    const [doorWidth, setDoorWidth] = useState(0.9);   // Standard door width
    const [doorHeight, setDoorHeight] = useState(2.1); // Standard door height

    // Flip states
    const [flipHand, setFlipHand] = useState(false); // false=Right, true=Left
    const [flipFacing, setFlipFacing] = useState(false); // false=Inward, true=Outward

    const addEntity = useAppStore((s) => s.addEntity);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);
    // Access BIM store for properties
    // Note: We need a way to access BIM store state inside the hook without subscribing if possible, 
    // or just subscribe to the property we need.
    // For now, we'll assume default or fetch from store directly in handler.

    const { raycaster, pointer, camera, gl } = useThree();

    const isActive = activeTool === 'door';

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    const snap = useCallback((point: THREE.Vector3): THREE.Vector3 => {
        const gs = 0.1;
        return applyOsnap(snapToGroundGrid(point, gs), camera, gl);
    }, [camera, gl]);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        const point = getGroundPoint();
        if (!point) return;

        const snapped = snap(point);
        const doorId = generateId();

        addEntity({
            id: doorId,
            type: 'door',
            name: `Door-${Date.now()}`,
            visible: true,
            locked: false,
            layer: activeLayerId,
            data: {
                position: [snapped.x, snapped.y, 0],
                width: doorWidth,
                height: doorHeight,
                thickness: 0.05,
                swingAngle: 0,
                swingDirection: flipHand ? 'left' : 'right',
                openDirection: flipFacing ? 'outward' : 'inward',
                frameWidth: 0.08,
                material: 'wood',
            } as DoorData,
        });

        // Tag on Placement
        // We need to import useBIMStore to check the property
        // Since we can't easily import it here without changing the file structure significantly (circular deps?),
        // we will assume true for now or try to access the global store if available.
        // In this codebase, stores are usually available via import.
        // Let's assume we can just create a tag.

        // Create a simple tag entity
        const tagId = generateId();
        addEntity({
            id: tagId,
            type: 'tag',
            name: `Tag-${doorId}`,
            visible: true,
            locked: false,
            layer: activeLayerId,
            data: {
                position: [snapped.x, snapped.y, 0], // Tag at door location
                targetId: doorId,
                text: '1', // Default mark
                offset: [0, 0, 0]
            }
        });

    }, [isActive, doorWidth, doorHeight, flipHand, flipFacing, getGroundPoint, snap, addEntity, activeLayerId]);

    const handlePointerMove = useCallback(() => {
        if (!isActive) return;

        const point = getGroundPoint();
        if (point) {
            // Grid-snapped dedupe: repeat cell reads keep the previous
            // reference so React bails out of the tool-scope re-render.
            setPreviewPosition((prev) => {
                const snapped = snap(point);
                return prev && prev.distanceToSquared(snapped) < 1e-6 ? prev : snapped;
            });
        }
    }, [isActive, getGroundPoint, snap]);

    // Handle Space key for flipping
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isActive) return;

            if (e.code === 'Space') {
                e.preventDefault(); // Prevent scrolling
                // Cycle: Right-In -> Left-In -> Left-Out -> Right-Out -> Right-In
                if (!flipHand && !flipFacing) {
                    setFlipHand(true); // Left-In
                } else if (flipHand && !flipFacing) {
                    setFlipFacing(true); // Left-Out
                } else if (flipHand && flipFacing) {
                    setFlipHand(false); // Right-Out
                } else {
                    setFlipFacing(false); // Right-In
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isActive, flipHand, flipFacing]);

    return {
        isActive,
        previewPosition,
        doorWidth,
        doorHeight,
        flipHand,
        flipFacing,
        setDoorWidth,
        setDoorHeight,
        handlePointerDown,
        handlePointerMove,
    };
}

/**
 * Door Mesh Component
 */
export const DoorMesh: React.FC<{
    data: DoorData;
    isSelected: boolean;
    isHovered: boolean;
    onClick: () => void;
}> = ({ data, isSelected, isHovered, onClick }) => {
    const color = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : '#8b4513';
    const frameColor = '#4a4a4a';

    return (
        <group
            position={[data.position[0], data.position[1], data.height / 2]}
            onClick={(e) => { e.stopPropagation(); onClick(); }}
        >
            {/* Z-up: width along local X, wall depth along Y, height along Z */}
            <group scale={[1, data.openDirection === 'outward' ? -1 : 1, 1]}>
                {/* Door frame */}
                <mesh position={[-(data.width / 2 + data.frameWidth / 2), 0, 0]}>
                    <boxGeometry args={[data.frameWidth, data.thickness + 0.02, data.height]} />
                    <meshStandardMaterial color={frameColor} />
                </mesh>
                <mesh position={[(data.width / 2 + data.frameWidth / 2), 0, 0]}>
                    <boxGeometry args={[data.frameWidth, data.thickness + 0.02, data.height]} />
                    <meshStandardMaterial color={frameColor} />
                </mesh>
                <mesh position={[0, 0, data.height / 2 - data.frameWidth / 2]}>
                    <boxGeometry args={[data.width + data.frameWidth * 2, data.thickness + 0.02, data.frameWidth]} />
                    <meshStandardMaterial color={frameColor} />
                </mesh>

                {/* Door panel with swing (hinge yaw about the Z level axis) */}
                <group
                    position={[data.swingDirection === 'left' ? data.width / 2 : -data.width / 2, 0, 0]}
                    rotation={[0, 0, (data.swingDirection === 'left' ? 1 : -1) * data.swingAngle * Math.PI / 180]}
                >
                    <mesh position={[data.swingDirection === 'left' ? -data.width / 2 : data.width / 2, 0, 0]}>
                        <boxGeometry args={[data.width, data.thickness, data.height - data.frameWidth]} />
                        <meshStandardMaterial color={color} />
                    </mesh>

                    {/* Door handle */}
                    <mesh
                        position={[
                            data.swingDirection === 'left' ? -data.width * 0.8 : data.width * 0.8,
                            data.thickness / 2 + 0.02,
                            0
                        ]}
                    >
                        <cylinderGeometry args={[0.02, 0.02, 0.12, 8]} />
                        <meshStandardMaterial color="#c0c0c0" metalness={0.8} roughness={0.2} />
                    </mesh>
                </group>

                {/* Swing arc indicator (when selected) */}
                {isSelected && (
                    <SwingArcIndicator
                        radius={data.width}
                        direction={data.swingDirection}
                        angle={90}
                        floorOffsetZ={data.height / 2}
                    />
                )}
            </group>
        </group>
    );
};

/**
 * Swing Arc Indicator
 */
const SwingArcIndicator: React.FC<{
    radius: number;
    direction: 'left' | 'right';
    angle: number;
    /** Distance from the door center (at height/2) down to the floor, along Z. */
    floorOffsetZ?: number;
}> = ({ radius, direction, angle, floorOffsetZ = 0 }) => {
    const points = useMemo(() => {
        const pts: THREE.Vector3[] = [];
        const segments = 16;
        const startAngle = 0;
        const endAngle = (direction === 'left' ? -1 : 1) * angle * Math.PI / 180;

        for (let i = 0; i <= segments; i++) {
            const a = startAngle + (endAngle - startAngle) * (i / segments);
            pts.push(new THREE.Vector3(
                Math.sin(a) * radius,
                Math.cos(a) * radius,
                -floorOffsetZ // at floor level (Z-up)
            ));
        }

        return pts;
    }, [radius, direction, angle]);

    return <Line points={points} color="#00ff00" lineWidth={1} dashed dashSize={0.1} gapSize={0.05} />;
};

// ============================================================================
// WINDOW TOOL
// ============================================================================

export interface WindowData {
    position: number[];
    width: number;
    height: number;
    sillHeight: number;     // Height from floor
    thickness: number;
    frameWidth: number;
    mullionCount: number;   // Vertical dividers
    transomCount: number;   // Horizontal dividers
    glazingType: 'single' | 'double' | 'triple';
    material: string;
    openDirection?: 'inward' | 'outward';
}

/**
 * Window Tool Hook
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useWindowTool() {
    const [previewPosition, setPreviewPosition] = useState<THREE.Vector3 | null>(null);
    const [windowWidth, setWindowWidth] = useState(1.2);
    const [windowHeight, setWindowHeight] = useState(1.5);
    const [sillHeight, setSillHeight] = useState(0.9);

    // Flip state
    const [flipFacing, setFlipFacing] = useState(false); // false=Outward (Standard), true=Inward (Flipped)

    const addEntity = useAppStore((s) => s.addEntity);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const { raycaster, pointer, camera, gl } = useThree();

    const isActive = activeTool === 'window';

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    const snap = useCallback((point: THREE.Vector3): THREE.Vector3 => {
        const gs = 0.1;
        return applyOsnap(snapToGroundGrid(point, gs), camera, gl);
    }, [camera, gl]);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        const point = getGroundPoint();
        if (!point) return;

        const snapped = snap(point);
        const windowId = generateId();

        addEntity({
            id: windowId,
            type: 'window',
            name: `Window-${Date.now()}`,
            visible: true,
            locked: false,
            layer: activeLayerId,
            data: {
                position: [snapped.x, snapped.y, 0],
                width: windowWidth,
                height: windowHeight,
                sillHeight,
                thickness: 0.1,
                frameWidth: 0.05,
                mullionCount: 1,
                transomCount: 0,
                glazingType: 'double',
                material: 'aluminum',
                openDirection: flipFacing ? 'inward' : 'outward',
            } as WindowData,
        });

        // Tag on Placement
        const tagId = generateId();
        addEntity({
            id: tagId,
            type: 'tag',
            name: `Tag-${windowId}`,
            visible: true,
            locked: false,
            layer: activeLayerId,
            data: {
                position: [snapped.x, snapped.y, 0], // Tag at window location
                targetId: windowId,
                text: 'W1', // Default mark
                offset: [0, 0, 0]
            }
        });

    }, [isActive, windowWidth, windowHeight, sillHeight, flipFacing, getGroundPoint, snap, addEntity, activeLayerId]);

    const handlePointerMove = useCallback(() => {
        if (!isActive) return;

        const point = getGroundPoint();
        if (point) {
            // Grid-snapped dedupe: repeat cell reads keep the previous
            // reference so React bails out of the tool-scope re-render.
            setPreviewPosition((prev) => {
                const snapped = snap(point);
                return prev && prev.distanceToSquared(snapped) < 1e-6 ? prev : snapped;
            });
        }
    }, [isActive, getGroundPoint, snap]);

    // Handle Space key for flipping
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isActive) return;

            if (e.code === 'Space') {
                e.preventDefault();
                setFlipFacing(prev => !prev);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isActive]);

    return {
        isActive,
        previewPosition,
        windowWidth,
        windowHeight,
        sillHeight,
        flipFacing,
        setWindowWidth,
        setWindowHeight,
        setSillHeight,
        handlePointerDown,
        handlePointerMove,
    };
}

/**
 * Window Mesh Component
 */
export const WindowMesh: React.FC<{
    data: WindowData;
    isSelected: boolean;
    isHovered: boolean;
    onClick: () => void;
}> = ({ data, isSelected, isHovered, onClick }) => {
    const frameColor = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : '#808080';
    const glassColor = '#87ceeb';

    const posY = data.sillHeight + data.height / 2;

    return (
        <group
            position={[data.position[0], data.position[1], posY]}
            onClick={(e) => { e.stopPropagation(); onClick(); }}
        >
            <group scale={[1, data.openDirection === 'inward' ? -1 : 1, 1]}>
                {/* Frame - outer (Z-up: height along +Z) */}
                <mesh>
                    <boxGeometry args={[data.width, data.thickness, data.height]} />
                    <meshStandardMaterial color={frameColor} />
                </mesh>

                {/* Glass panels */}
                <mesh position={[0, 0, 0]}>
                    <boxGeometry args={[
                        data.width - data.frameWidth * 2,
                        data.thickness * 0.3,
                        data.height - data.frameWidth * 2
                    ]} />
                    <meshStandardMaterial
                        color={glassColor}
                        transparent
                        opacity={0.4}
                        metalness={0.9}
                        roughness={0.1}
                    />
                </mesh>

                {/* Mullions (vertical dividers) */}
                {data.mullionCount > 0 && Array.from({ length: data.mullionCount }).map((_, i) => {
                    const x = ((i + 1) / (data.mullionCount + 1) - 0.5) * (data.width - data.frameWidth * 2);
                    return (
                        <mesh key={`mullion-${i}`} position={[x, 0, 0]}>
                            <boxGeometry args={[data.frameWidth * 0.5, data.thickness * 0.5, data.height - data.frameWidth * 2]} />
                            <meshStandardMaterial color={frameColor} />
                        </mesh>
                    );
                })}

                {/* Transoms (horizontal dividers) */}
                {data.transomCount > 0 && Array.from({ length: data.transomCount }).map((_, i) => {
                    const y = ((i + 1) / (data.transomCount + 1) - 0.5) * (data.height - data.frameWidth * 2);
                    return (
                        <mesh key={`transom-${i}`} position={[0, 0, y]}>
                            <boxGeometry args={[data.width - data.frameWidth * 2, data.thickness * 0.5, data.frameWidth * 0.5]} />
                            <meshStandardMaterial color={frameColor} />
                        </mesh>
                    );
                })}
            </group>
        </group>
    );
};

// ============================================================================
// FLOOR/SLAB TOOL
// ============================================================================

export interface FloorData {
    vertices: number[][];   // Boundary polygon vertices
    thickness: number;
    elevation: number;      // Floor level
    material: string;
    isStructural: boolean;
}

/**
 * Floor Tool Hook - Draw boundary polygon
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useFloorTool() {
    const [vertices, setVertices] = useState<THREE.Vector3[]>([]);
    const [previewPoint, setPreviewPoint] = useState<THREE.Vector3 | null>(null);
    const [thickness, setThickness] = useState(0.3);
    const [elevation, setElevation] = useState(0);

    const addEntity = useAppStore((s) => s.addEntity);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const { raycaster, pointer, camera, gl } = useThree();

    const isActive = activeTool === 'floor';

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    const snap = useCallback((point: THREE.Vector3): THREE.Vector3 => {
        const gs = 0.5;
        return snapToGroundGrid(point, gs, elevation);
    }, [elevation]);

    const completeFloor = useCallback(() => {
        if (vertices.length >= 3) {
            addEntity({
                id: generateId(),
                type: 'floor',
                name: `Floor-${Date.now()}`,
                visible: true,
                locked: false,
                layer: activeLayerId,
                data: {
                    vertices: vertices.map(v => [v.x, v.y, v.z]),
                    thickness,
                    elevation,
                    material: 'concrete',
                    isStructural: true,
                } as FloorData,
            });
        }
        setVertices([]);
        setPreviewPoint(null);
    }, [vertices, thickness, elevation, addEntity, activeLayerId]);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        const point = getGroundPoint();
        if (!point) return;

        const snapped = snap(point);
        setVertices([...vertices, snapped]);
    }, [isActive, vertices, getGroundPoint, snap]);

    const handlePointerMove = useCallback(() => {
        if (!isActive) return;

        const point = getGroundPoint();
        if (point) {
            // Grid-snapped dedupe: repeat cell reads keep the previous
            // reference so React bails out of the tool-scope re-render.
            setPreviewPoint((prev) => {
                const snapped = snap(point);
                return prev && prev.distanceToSquared(snapped) < 1e-6 ? prev : snapped;
            });
        }
    }, [isActive, getGroundPoint, snap]);

    const handleDoubleClick = useCallback(() => {
        if (isActive && vertices.length >= 3) {
            completeFloor();
        }
    }, [isActive, vertices.length, completeFloor]);

    // Keyboard handlers
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isActive) return;

            if (e.key === 'Escape') {
                setVertices([]);
                setPreviewPoint(null);
            } else if (e.key === 'Enter' && vertices.length >= 3) {
                completeFloor();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isActive, vertices.length, completeFloor]);

    return {
        isActive,
        vertices,
        previewPoint,
        thickness,
        elevation,
        setThickness,
        setElevation,
        handlePointerDown,
        handlePointerMove,
        handleDoubleClick,
    };
}

/**
 * Floor Mesh Component
 */
export const FloorMesh: React.FC<{
    data: FloorData;
    isSelected: boolean;
    isHovered: boolean;
    onClick: () => void;
}> = ({ data, isSelected, isHovered, onClick }) => {
    const color = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : '#808080';

    const geometry = useMemo(() => {
        if (data.vertices.length < 3) return null;

        // Create shape from vertices in the XY ground plane (Z-up); the
        // extrusion runs along +Z, so no corrective rotation is needed.
        const shape = new THREE.Shape();
        const first = data.vertices[0];
        shape.moveTo(first[0], first[1]);

        for (let i = 1; i < data.vertices.length; i++) {
            shape.lineTo(data.vertices[i][0], data.vertices[i][1]);
        }
        shape.closePath();

        return new THREE.ExtrudeGeometry(shape, {
            depth: data.thickness,
            bevelEnabled: false,
        });
    }, [data.vertices, data.thickness]);

    if (!geometry) return null;

    return (
        <mesh
            geometry={geometry}
            position={[0, 0, data.elevation]}
            onClick={(e) => { e.stopPropagation(); onClick(); }}
        >
            <meshStandardMaterial color={color} />
        </mesh>
    );
};

/**
 * Floor Preview Component
 */
export const FloorPreview: React.FC<{
    vertices: THREE.Vector3[];
    previewPoint: THREE.Vector3 | null;
}> = ({ vertices, previewPoint }) => {
    const points = useMemo(() => {
        const pts = [...vertices];
        if (previewPoint) pts.push(previewPoint);
        if (pts.length > 2) pts.push(pts[0]); // Close the polygon
        return pts;
    }, [vertices, previewPoint]);

    if (points.length < 2) return null;

    return (
        <>
            <Line points={points} color="#00ff00" lineWidth={2} />
            {vertices.map((v, i) => (
                <mesh key={i} position={v}>
                    <sphereGeometry args={[0.1, 8, 8]} />
                    <meshBasicMaterial color="#ffff00" />
                </mesh>
            ))}
        </>
    );
};

// ============================================================================
// COLUMN TOOL
// ============================================================================

export interface ColumnData {
    position: number[];
    width: number;
    depth: number;
    height: number;
    baseLevel: number;
    topLevel: number;
    shape: 'rectangular' | 'circular';
    material: string;
}

/**
 * Column Tool Hook
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useColumnTool() {
    const [previewPosition, setPreviewPosition] = useState<THREE.Vector3 | null>(null);
    const [columnWidth, setColumnWidth] = useState(0.4);
    const [columnDepth, setColumnDepth] = useState(0.4);
    const [columnHeight, setColumnHeight] = useState(3.0);
    const [shape, setShape] = useState<'rectangular' | 'circular'>('rectangular');

    const addEntity = useAppStore((s) => s.addEntity);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const { raycaster, pointer, camera, gl } = useThree();

    const isActive = activeTool === 'column';

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    const snap = useCallback((point: THREE.Vector3): THREE.Vector3 => {
        const gs = 0.5;
        return applyOsnap(snapToGroundGrid(point, gs), camera, gl);
    }, [camera, gl]);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        const point = getGroundPoint();
        if (!point) return;

        const snapped = snap(point);

        addEntity({
            id: generateId(),
            type: 'column',
            name: `Column-${Date.now()}`,
            visible: true,
            locked: false,
            layer: activeLayerId,
            data: {
                position: [snapped.x, snapped.y, columnHeight / 2],
                width: columnWidth,
                depth: columnDepth,
                height: columnHeight,
                baseLevel: 0,
                topLevel: columnHeight,
                shape,
                material: 'concrete',
            } as ColumnData,
        });
    }, [isActive, columnWidth, columnDepth, columnHeight, shape, getGroundPoint, snap, addEntity, activeLayerId]);

    const handlePointerMove = useCallback(() => {
        if (!isActive) return;

        const point = getGroundPoint();
        if (point) {
            // Grid-snapped dedupe: repeat cell reads keep the previous
            // reference so React bails out of the tool-scope re-render.
            setPreviewPosition((prev) => {
                const snapped = snap(point);
                return prev && prev.distanceToSquared(snapped) < 1e-6 ? prev : snapped;
            });
        }
    }, [isActive, getGroundPoint, snap]);

    return {
        isActive,
        previewPosition,
        columnWidth,
        columnDepth,
        columnHeight,
        shape,
        setColumnWidth,
        setColumnDepth,
        setColumnHeight,
        setShape,
        handlePointerDown,
        handlePointerMove,
    };
}

/**
 * Column Mesh Component
 */
export const ColumnMesh: React.FC<{
    data: ColumnData;
    isSelected: boolean;
    isHovered: boolean;
    onClick: () => void;
}> = ({ data, isSelected, isHovered, onClick }) => {
    const color = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : '#707070';

    return (
        <mesh
            position={[data.position[0], data.position[1], data.position[2]]}
            rotation={data.shape === 'circular' ? [Math.PI / 2, 0, 0] : [0, 0, 0]}
            onClick={(e) => { e.stopPropagation(); onClick(); }}
        >
            {data.shape === 'circular' ? (
                // Z-up: rotate the Y-axis cylinder onto +Z
                <cylinderGeometry args={[data.width / 2, data.width / 2, data.height, 24]} />
            ) : (
                <boxGeometry args={[data.width, data.depth, data.height]} />
            )}
            <meshStandardMaterial color={color} />
        </mesh>
    );
};

// ============================================================================
// ROOF TOOL
// ============================================================================

export interface RoofData {
    vertices: number[][];   // Boundary polygon vertices
    slope: number;          // Slope in degrees
    overhang: number;       // Overhang in meters
    thickness: number;
    elevation: number;      // Base elevation
    roofType: 'Flat' | 'Gable' | 'Hip' | 'Shed';
    material: string;
}

/**
 * Roof Tool Hook - Draw boundary polygon
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useRoofTool() {
    const [vertices, setVertices] = useState<THREE.Vector3[]>([]);
    const [previewPoint, setPreviewPoint] = useState<THREE.Vector3 | null>(null);
    const [slope, setSlope] = useState(30); // Degrees
    const [overhang, setOverhang] = useState(0.3); // Meters
    const [thickness, setThickness] = useState(0.3);
    const [elevation, setElevation] = useState(3.0); // Default roof level

    const addEntity = useAppStore((s) => s.addEntity);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const { raycaster, pointer, camera, gl } = useThree();

    const isActive = activeTool === 'roof';

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    const snap = useCallback((point: THREE.Vector3): THREE.Vector3 => {
        const gs = 0.5;
        return snapToGroundGrid(point, gs, elevation);
    }, [elevation]);

    const completeRoof = useCallback(() => {
        if (vertices.length >= 3) {
            addEntity({
                id: generateId(),
                type: 'roof',
                name: `Roof-${Date.now()}`,
                visible: true,
                locked: false,
                layer: activeLayerId,
                data: {
                    vertices: vertices.map(v => [v.x, v.y, v.z]),
                    slope,
                    overhang,
                    thickness,
                    elevation,
                    roofType: 'Flat', // Default to flat for now, logic can be enhanced
                    material: 'shingle',
                } as RoofData,
            });
        }
        setVertices([]);
        setPreviewPoint(null);
    }, [vertices, slope, overhang, thickness, elevation, addEntity, activeLayerId]);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        const point = getGroundPoint();
        if (!point) return;

        const snapped = snap(point);
        setVertices([...vertices, snapped]);
    }, [isActive, vertices, getGroundPoint, snap]);

    const handlePointerMove = useCallback(() => {
        if (!isActive) return;

        const point = getGroundPoint();
        if (point) {
            // Grid-snapped dedupe: repeat cell reads keep the previous
            // reference so React bails out of the tool-scope re-render.
            setPreviewPoint((prev) => {
                const snapped = snap(point);
                return prev && prev.distanceToSquared(snapped) < 1e-6 ? prev : snapped;
            });
        }
    }, [isActive, getGroundPoint, snap]);

    const handleDoubleClick = useCallback(() => {
        if (isActive && vertices.length >= 3) {
            completeRoof();
        }
    }, [isActive, vertices.length, completeRoof]);

    // Keyboard handlers
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isActive) return;

            if (e.key === 'Escape') {
                setVertices([]);
                setPreviewPoint(null);
            } else if (e.key === 'Enter' && vertices.length >= 3) {
                completeRoof();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isActive, vertices.length, completeRoof]);

    return {
        isActive,
        vertices,
        previewPoint,
        slope,
        overhang,
        thickness,
        elevation,
        setSlope,
        setOverhang,
        setThickness,
        setElevation,
        handlePointerDown,
        handlePointerMove,
        handleDoubleClick,
    };
}

/**
 * Roof Mesh Component
 */
export const RoofMesh: React.FC<{
    data: RoofData;
    isSelected: boolean;
    isHovered: boolean;
    onClick: () => void;
}> = ({ data, isSelected, isHovered, onClick }) => {
    const color = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : '#a52a2a'; // Brownish for roof

    const geometry = useMemo(() => {
        if (data.vertices.length < 3) return null;

        // Create shape from vertices in the XY ground plane (Z-up); the
        // extrusion runs along +Z, so no corrective rotation is needed.
        const shape = new THREE.Shape();
        const first = data.vertices[0];
        shape.moveTo(first[0], first[1]);

        for (let i = 1; i < data.vertices.length; i++) {
            shape.lineTo(data.vertices[i][0], data.vertices[i][1]);
        }
        shape.closePath();

        // Minimap schematic: the roof's footprint slab. The main viewport
        // renders the full 3D roof shape from RoofGeometryData; from the
        // plan-view minimap only the footprint is visible either way.
        return new THREE.ExtrudeGeometry(shape, {
            depth: data.thickness,
            bevelEnabled: false,
        });
    }, [data.vertices, data.thickness]);

    if (!geometry) return null;

    return (
        <mesh
            geometry={geometry}
            position={[0, 0, data.elevation]}
            onClick={(e) => { e.stopPropagation(); onClick(); }}
        >
            <meshStandardMaterial color={color} />
        </mesh>
    );
};

/**
 * Roof Preview Component (Reusing FloorPreview logic for now)
 */
export const RoofPreview: React.FC<{
    vertices: THREE.Vector3[];
    previewPoint: THREE.Vector3 | null;
}> = ({ vertices, previewPoint }) => {
    const points = useMemo(() => {
        const pts = [...vertices];
        if (previewPoint) pts.push(previewPoint);
        if (pts.length > 2) pts.push(pts[0]); // Close the polygon
        return pts;
    }, [vertices, previewPoint]);

    if (points.length < 2) return null;

    return (
        <>
            <Line points={points} color="#ff0000" lineWidth={2} />
            {vertices.map((v, i) => (
                <mesh key={i} position={v}>
                    <sphereGeometry args={[0.1, 8, 8]} />
                    <meshBasicMaterial color="#ffff00" />
                </mesh>
            ))}
        </>
    );
};

// ============================================================================
// CEILING TOOL
// ============================================================================

export interface CeilingData {
    vertices: number[][];   // Boundary polygon vertices
    elevation: number;      // Height from level
    thickness: number;
    ceilingType: 'Grid' | 'GWB' | 'Plain';
    material: string;
}

/**
 * Ceiling Tool Hook - Draw boundary polygon
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useCeilingTool() {
    const [vertices, setVertices] = useState<THREE.Vector3[]>([]);
    const [previewPoint, setPreviewPoint] = useState<THREE.Vector3 | null>(null);
    const [elevation, setElevation] = useState(2.6); // Default ceiling height
    const [thickness, setThickness] = useState(0.05); // Default thickness

    const addEntity = useAppStore((s) => s.addEntity);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const { raycaster, pointer, camera, gl } = useThree();

    const isActive = activeTool === 'ceiling';

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    const snap = useCallback((point: THREE.Vector3): THREE.Vector3 => {
        const gs = 0.5;
        return snapToGroundGrid(point, gs, elevation);
    }, [elevation]);

    const completeCeiling = useCallback(() => {
        if (vertices.length >= 3) {
            addEntity({
                id: generateId(),
                type: 'ceiling',
                name: `Ceiling-${Date.now()}`,
                visible: true,
                locked: false,
                layer: activeLayerId,
                data: {
                    vertices: vertices.map(v => [v.x, v.y, v.z]),
                    elevation,
                    thickness,
                    ceilingType: 'Grid', // Default
                    material: 'acoustic_tile',
                } as CeilingData,
            });
        }
        setVertices([]);
        setPreviewPoint(null);
    }, [vertices, elevation, thickness, addEntity, activeLayerId]);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        const point = getGroundPoint();
        if (!point) return;

        const snapped = snap(point);
        setVertices([...vertices, snapped]);
    }, [isActive, vertices, getGroundPoint, snap]);

    const handlePointerMove = useCallback(() => {
        if (!isActive) return;

        const point = getGroundPoint();
        if (point) {
            // Grid-snapped dedupe: repeat cell reads keep the previous
            // reference so React bails out of the tool-scope re-render.
            setPreviewPoint((prev) => {
                const snapped = snap(point);
                return prev && prev.distanceToSquared(snapped) < 1e-6 ? prev : snapped;
            });
        }
    }, [isActive, getGroundPoint, snap]);

    const handleDoubleClick = useCallback(() => {
        if (isActive && vertices.length >= 3) {
            completeCeiling();
        }
    }, [isActive, vertices.length, completeCeiling]);

    // Keyboard handlers
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isActive) return;

            if (e.key === 'Escape') {
                setVertices([]);
                setPreviewPoint(null);
            } else if (e.key === 'Enter' && vertices.length >= 3) {
                completeCeiling();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isActive, vertices.length, completeCeiling]);

    return {
        isActive,
        vertices,
        previewPoint,
        elevation,
        thickness,
        setElevation,
        setThickness,
        handlePointerDown,
        handlePointerMove,
        handleDoubleClick,
    };
}

/**
 * Ceiling Mesh Component
 */
export const CeilingMesh: React.FC<{
    data: CeilingData;
    isSelected: boolean;
    isHovered: boolean;
    onClick: () => void;
}> = ({ data, isSelected, isHovered, onClick }) => {
    const color = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : '#e0e0e0'; // Light grey for ceiling

    const geometry = useMemo(() => {
        if (data.vertices.length < 3) return null;

        // Create shape from vertices in the XY ground plane (Z-up); the
        // extrusion runs along +Z, so no corrective rotation is needed.
        const shape = new THREE.Shape();
        const first = data.vertices[0];
        shape.moveTo(first[0], first[1]);

        for (let i = 1; i < data.vertices.length; i++) {
            shape.lineTo(data.vertices[i][0], data.vertices[i][1]);
        }
        shape.closePath();

        return new THREE.ExtrudeGeometry(shape, {
            depth: data.thickness,
            bevelEnabled: false,
        });
    }, [data.vertices, data.thickness]);

    if (!geometry) return null;

    return (
        <mesh
            geometry={geometry}
            position={[0, 0, data.elevation]}
            onClick={(e) => { e.stopPropagation(); onClick(); }}
        >
            <meshStandardMaterial color={color} side={THREE.DoubleSide} defines={BACKFACE_DEFINES} />
        </mesh>
    );
};

/**
 * Ceiling Preview Component (Reusing RoofPreview logic)
 */
export const CeilingPreview: React.FC<{
    vertices: THREE.Vector3[];
    previewPoint: THREE.Vector3 | null;
}> = ({ vertices, previewPoint }) => {
    const points = useMemo(() => {
        const pts = [...vertices];
        if (previewPoint) pts.push(previewPoint);
        if (pts.length > 2) pts.push(pts[0]); // Close the polygon
        return pts;
    }, [vertices, previewPoint]);

    if (points.length < 2) return null;

    return (
        <>
            <Line points={points} color="#ff00ff" lineWidth={2} />
            {vertices.map((v, i) => (
                <mesh key={i} position={v}>
                    <sphereGeometry args={[0.08, 8, 8]} />
                    <meshBasicMaterial color="#ff00ff" />
                </mesh>
            ))}
        </>
    );
};

// ============================================================================
// STAIRS TOOL
// ============================================================================

export interface StairData {
    startPoint: number[];
    endPoint: number[];
    width: number;
    treadDepth: number;
    riserHeight: number;
    totalHeight: number;
    stepCount: number;
    material: string;
}

/**
 * Stair Tool Hook - Straight Run
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useStairTool() {
    const [startPoint, setStartPoint] = useState<THREE.Vector3 | null>(null);
    const [previewEndPoint, setPreviewEndPoint] = useState<THREE.Vector3 | null>(null);
    const [width, setWidth] = useState(1.0); // 1m width
    const [treadDepth, setTreadDepth] = useState(0.28); // 28cm tread
    const [riserHeight, setRiserHeight] = useState(0.17); // 17cm riser

    const addEntity = useAppStore((s) => s.addEntity);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const { raycaster, pointer, camera, gl } = useThree();

    const isActive = activeTool === 'stair';

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    const snap = useCallback((point: THREE.Vector3): THREE.Vector3 => {
        const gs = 0.1;
        return applyOsnap(snapToGroundGrid(point, gs), camera, gl);
    }, [camera, gl]);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        const point = getGroundPoint();
        if (!point) return;
        const snapped = snap(point);

        if (!startPoint) {
            setStartPoint(snapped);
        } else {
            // Finish placement
            const endPoint = snapped;
            const distance = new THREE.Vector2(endPoint.x - startPoint.x, endPoint.y - startPoint.y).length();
            const stepCount = Math.max(1, Math.round(distance / treadDepth));
            const totalHeight = stepCount * riserHeight;

            addEntity({
                id: generateId(),
                type: 'stair',
                name: `Stair-${Date.now()}`,
                visible: true,
                locked: false,
                layer: activeLayerId,
                data: {
                    startPoint: [startPoint.x, startPoint.y, startPoint.z],
                    endPoint: [endPoint.x, endPoint.y, endPoint.z],
                    width,
                    treadDepth,
                    riserHeight,
                    totalHeight,
                    stepCount,
                    material: 'concrete',
                } as StairData,
            });

            setStartPoint(null);
            setPreviewEndPoint(null);
        }
    }, [isActive, startPoint, width, treadDepth, riserHeight, getGroundPoint, snap, addEntity, activeLayerId]);

    const handlePointerMove = useCallback(() => {
        if (!isActive) return;

        const point = getGroundPoint();
        if (point) {
            // Grid-snapped dedupe: repeat cell reads keep the previous
            // reference so React bails out of the tool-scope re-render.
            setPreviewEndPoint((prev) => {
                const snapped = snap(point);
                return prev && prev.distanceToSquared(snapped) < 1e-6 ? prev : snapped;
            });
        }
    }, [isActive, getGroundPoint, snap]);

    // Keyboard handlers
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isActive) return;

            if (e.key === 'Escape') {
                setStartPoint(null);
                setPreviewEndPoint(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isActive]);

    return {
        isActive,
        startPoint,
        previewEndPoint,
        width,
        treadDepth,
        riserHeight,
        setWidth,
        setTreadDepth,
        setRiserHeight,
        handlePointerDown,
        handlePointerMove,
    };
}

/**
 * Stair Mesh Component
 */
export const StairMesh: React.FC<{
    data: StairData;
    isSelected: boolean;
    isHovered: boolean;
    onClick: () => void;
}> = ({ data, isSelected, isHovered, onClick }) => {
    const color = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : '#808080';

    const steps = useMemo(() => {
        const start = new THREE.Vector3(...data.startPoint);
        const end = new THREE.Vector3(...data.endPoint);
        const direction = new THREE.Vector3().subVectors(end, start).normalize();
        const stepMeshes = [];
        for (let i = 0; i < data.stepCount; i++) {
            const stepPos = start.clone()
                .add(direction.clone().multiplyScalar((i + 0.5) * data.treadDepth))
                .add(new THREE.Vector3(0, 0, (i + 0.5) * data.riserHeight));

            // Heading in the XY plane (rotation about the Z level axis)
            const angle = Math.atan2(direction.y, direction.x);

            stepMeshes.push(
                <mesh
                    key={i}
                    position={[stepPos.x, stepPos.y, stepPos.z]}
                    rotation={[0, 0, angle]}
                >
                    <boxGeometry args={[data.width, data.treadDepth, data.riserHeight]} />
                    <meshStandardMaterial color={color} />
                </mesh>
            );
        }
        return stepMeshes;
    }, [data, color]);

    return (
        <group onClick={(e) => { e.stopPropagation(); onClick(); }}>
            {steps}
        </group>
    );
};

/**
 * Stair Preview Component
 */
export const StairPreview: React.FC<{
    startPoint: THREE.Vector3;
    endPoint: THREE.Vector3;
    width: number;
    treadDepth: number;
    riserHeight: number;
}> = ({ startPoint, endPoint, width, treadDepth, riserHeight }) => {
    const preview = useMemo(() => {
        const distance = new THREE.Vector2(endPoint.x - startPoint.x, endPoint.y - startPoint.y).length();
        const stepCount = Math.max(1, Math.round(distance / treadDepth));

        // Reuse StairMesh logic or simplify for preview
        // For preview, let's just show the bounding box of the stair run
        const direction = new THREE.Vector3().subVectors(endPoint, startPoint).normalize();
        const angle = Math.atan2(direction.y, direction.x);

        const totalLen = stepCount * treadDepth;
        const totalH = stepCount * riserHeight;

        const center = startPoint.clone()
            .add(direction.clone().multiplyScalar(totalLen / 2))
            .add(new THREE.Vector3(0, 0, totalH / 2));

        return (
            <group position={center} rotation={[0, 0, angle]}>
                <mesh>
                    <boxGeometry args={[width, totalLen, totalH]} />
                    <meshBasicMaterial color="#00ff00" transparent opacity={0.3} />
                </mesh>
                <Line
                    points={[
                        [-width / 2, -totalLen / 2, -totalH / 2], [-width / 2, totalLen / 2, -totalH / 2],
                        [width / 2, totalLen / 2, -totalH / 2], [width / 2, -totalLen / 2, -totalH / 2],
                        [-width / 2, -totalLen / 2, -totalH / 2]
                    ]}
                    color="#00ff00"
                />
            </group>
        );
    }, [startPoint, endPoint, width, treadDepth, riserHeight]);

    return preview;
};

// ============================================================================
// RAILING TOOL
// ============================================================================

export interface RailingData {
    path: number[][]; // 3D path points
    height: number;
    offset: number;
    railingType: 'Pipe' | 'Glass' | 'Rectangular';
    material: string;
}

/**
 * Railing Tool Hook - Sketch Path
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useRailingTool() {
    const [points, setPoints] = useState<THREE.Vector3[]>([]);
    const [previewPoint, setPreviewPoint] = useState<THREE.Vector3 | null>(null);
    const [height, setHeight] = useState(1.1); // 1.1m default height
    const [offset, setOffset] = useState(0);

    const addEntity = useAppStore((s) => s.addEntity);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const { raycaster, pointer, camera, gl } = useThree();

    const isActive = activeTool === 'railing';

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    const snap = useCallback((point: THREE.Vector3): THREE.Vector3 => {
        const gs = 0.1;
        return applyOsnap(snapToGroundGrid(point, gs), camera, gl);
    }, [camera, gl]);

    const completeRailing = useCallback(() => {
        if (points.length >= 2) {
            addEntity({
                id: generateId(),
                type: 'railing',
                name: `Railing-${Date.now()}`,
                visible: true,
                locked: false,
                layer: activeLayerId,
                data: {
                    path: points.map(p => [p.x, p.y, p.z]),
                    height,
                    offset,
                    railingType: 'Pipe',
                    material: 'steel',
                } as RailingData,
            });
        }
        setPoints([]);
        setPreviewPoint(null);
    }, [points, height, offset, addEntity, activeLayerId]);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        const point = getGroundPoint();
        if (!point) return;

        const snapped = snap(point);
        setPoints([...points, snapped]);
    }, [isActive, points, getGroundPoint, snap]);

    const handlePointerMove = useCallback(() => {
        if (!isActive) return;

        const point = getGroundPoint();
        if (point) {
            // Grid-snapped dedupe: repeat cell reads keep the previous
            // reference so React bails out of the tool-scope re-render.
            setPreviewPoint((prev) => {
                const snapped = snap(point);
                return prev && prev.distanceToSquared(snapped) < 1e-6 ? prev : snapped;
            });
        }
    }, [isActive, getGroundPoint, snap]);

    const handleDoubleClick = useCallback(() => {
        if (isActive && points.length >= 2) {
            completeRailing();
        }
    }, [isActive, points.length, completeRailing]);

    // Keyboard handlers
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isActive) return;

            if (e.key === 'Escape') {
                setPoints([]);
                setPreviewPoint(null);
            } else if (e.key === 'Enter' && points.length >= 2) {
                completeRailing();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isActive, points.length, completeRailing]);

    return {
        isActive,
        points,
        previewPoint,
        height,
        offset,
        setHeight,
        setOffset,
        handlePointerDown,
        handlePointerMove,
        handleDoubleClick,
    };
}

/**
 * Railing Mesh Component
 */
export const RailingMesh: React.FC<{
    data: RailingData;
    isSelected: boolean;
    isHovered: boolean;
    onClick: () => void;
}> = ({ data, isSelected, isHovered, onClick }) => {
    const color = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : '#a0a0a0';

    const geometry = useMemo(() => {
        if (data.path.length < 2) return null;

        const pathPoints = data.path.map(p => new THREE.Vector3(...p));
        const curve = new THREE.CatmullRomCurve3(pathPoints, false, 'catmullrom', 0.05);

        // Top Rail
        const topRailGeometry = new THREE.TubeGeometry(curve, pathPoints.length * 10, 0.03, 8, false);

        // Balusters
        // Place balusters every X meters along the path
        const balusterGeometries: THREE.BufferGeometry[] = [];
        const length = curve.getLength();
        const spacing = 0.2; // 20cm spacing
        const count = Math.floor(length / spacing);

        for (let i = 0; i <= count; i++) {
            const t = i / count;
            const pos = curve.getPointAt(t);
            // Simple vertical baluster (Z-up: rotate the Y-axis cylinder
            // onto +Z, then center it on the level axis above the path point)
            const balusterGeo = new THREE.CylinderGeometry(0.01, 0.01, data.height, 8);
            balusterGeo.rotateX(Math.PI / 2);
            balusterGeo.translate(pos.x, pos.y, pos.z + data.height / 2);
            balusterGeometries.push(balusterGeo);
        }

        // Merge geometries (conceptually - here we might return a group or just the top rail for simplicity first, 
        // but let's try to return a group of meshes or a merged mesh if possible. 
        // React-three-fiber makes it easier to return a group.)

        return { topRail: topRailGeometry, balusters: balusterGeometries };

    }, [data.path, data.height]);

    if (!geometry) return null;

    return (
        <group onClick={(e) => { e.stopPropagation(); onClick(); }}>
            {/* Top Rail (Z-up: height rides the level axis) */}
            <mesh geometry={geometry.topRail} position={[0, 0, data.height]}>
                <meshStandardMaterial color={color} />
            </mesh>

            {/* Balusters */}
            {geometry.balusters.map((geo, i) => (
                <mesh key={i} geometry={geo}>
                    <meshStandardMaterial color={color} />
                </mesh>
            ))}
        </group>
    );
};

/**
 * Railing Preview Component
 */
export const RailingPreview: React.FC<{
    points: THREE.Vector3[];
    previewPoint: THREE.Vector3 | null;
}> = ({ points, previewPoint }) => {
    const allPoints = useMemo(() => {
        const pts = [...points];
        if (previewPoint) pts.push(previewPoint);
        return pts;
    }, [points, previewPoint]);

    if (allPoints.length < 2) return null;

    return (
        <>
            <Line points={allPoints} color="#ff00ff" lineWidth={2} />
            {allPoints.map((v, i) => (
                <mesh key={i} position={v}>
                    <sphereGeometry args={[0.05, 8, 8]} />
                    <meshBasicMaterial color="#ff00ff" />
                </mesh>
            ))}
        </>
    );
};

// ============================================================================
// RAMP TOOL
// ============================================================================

export interface RampData {
    startPoint: number[];
    endPoint: number[];
    width: number;
    slope: number; // 1:x
    baseLevelId: string;
    topLevelId: string;
}

/**
 * Ramp Tool Hook - Straight Run
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useRampTool() {
    const [startPoint, setStartPoint] = useState<THREE.Vector3 | null>(null);
    const [previewEndPoint, setPreviewEndPoint] = useState<THREE.Vector3 | null>(null);
    const [width, setWidth] = useState(1.0); // 1m width
    const [slope, setSlope] = useState(12); // 1:12

    const addEntity = useAppStore((s) => s.addEntity);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const { raycaster, pointer, camera, gl } = useThree();

    const isActive = activeTool === 'ramp';

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    const snap = useCallback((point: THREE.Vector3): THREE.Vector3 => {
        const gs = 0.1;
        return applyOsnap(snapToGroundGrid(point, gs), camera, gl);
    }, [camera, gl]);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        const point = getGroundPoint();
        if (!point) return;
        const snapped = snap(point);

        if (!startPoint) {
            setStartPoint(snapped);
        } else {
            // Finish placement
            addEntity({
                id: generateId(),
                type: 'ramp',
                name: `Ramp-${Date.now()}`,
                visible: true,
                locked: false,
                layer: activeLayerId,
                data: {
                    startPoint: [startPoint.x, startPoint.y, startPoint.z],
                    endPoint: [snapped.x, snapped.y, snapped.z],
                    width,
                    slope,
                    baseLevelId: 'Level 1',
                    topLevelId: 'Level 2',
                } as RampData,
            });

            setStartPoint(null);
            setPreviewEndPoint(null);
        }
    }, [isActive, startPoint, width, slope, getGroundPoint, snap, addEntity, activeLayerId]);

    const handlePointerMove = useCallback(() => {
        if (!isActive) return;

        const point = getGroundPoint();
        if (point) {
            // Grid-snapped dedupe: repeat cell reads keep the previous
            // reference so React bails out of the tool-scope re-render.
            setPreviewEndPoint((prev) => {
                const snapped = snap(point);
                return prev && prev.distanceToSquared(snapped) < 1e-6 ? prev : snapped;
            });
        }
    }, [isActive, getGroundPoint, snap]);

    // Keyboard handlers
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isActive) return;

            if (e.key === 'Escape') {
                setStartPoint(null);
                setPreviewEndPoint(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isActive]);

    return {
        isActive,
        startPoint,
        previewEndPoint,
        width,
        slope,
        setWidth,
        setSlope,
        handlePointerDown,
        handlePointerMove,
    };
}

/**
 * Ramp Mesh Component
 */
export const RampMesh: React.FC<{
    data: RampData;
    isSelected: boolean;
    isHovered: boolean;
    onClick: () => void;
}> = ({ data, isSelected, isHovered, onClick }) => {
    const color = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : '#909090';

    const geometry = useMemo(() => {
        const start = new THREE.Vector3(...data.startPoint);
        const end = new THREE.Vector3(...data.endPoint);
        const length = new THREE.Vector2(end.x - start.x, end.y - start.y).length();
        const height = length / data.slope;

        // Create a wedge shape: profile in the run/height plane, extruded
        // sideways along +Z, then rotated onto the width axis (see mesh).
        const shape = new THREE.Shape();
        shape.moveTo(0, 0);
        shape.lineTo(length, height);
        shape.lineTo(length, 0);
        shape.closePath();

        const extrudeSettings = {
            depth: data.width,
            bevelEnabled: false
        };

        const geo = new THREE.ExtrudeGeometry(shape, extrudeSettings);

        // Extrusion is +Z; width must run along local Y. Then center.
        geo.rotateX(Math.PI / 2);
        geo.center();

        return geo;
    }, [data]);

    // Calculate position and rotation
    const { position, rotation } = useMemo(() => {
        const start = new THREE.Vector3(...data.startPoint);
        const end = new THREE.Vector3(...data.endPoint);
        const direction = new THREE.Vector3().subVectors(end, start).normalize();
        const angle = Math.atan2(direction.y, direction.x);

        const length = new THREE.Vector2(end.x - start.x, end.y - start.y).length();
        const height = length / data.slope;

        const center = start.clone()
            .add(direction.clone().multiplyScalar(length / 2))
            .add(new THREE.Vector3(0, 0, height / 2));

        return {
            position: [center.x, center.y, center.z],
            // No +PI/2 correction: the wedge profile rises along +Z and runs
            // along local +X, aligned directly by the Z-axis yaw.
            rotation: [0, 0, angle]
        };
    }, [data]);

    return (
        <mesh
            geometry={geometry}
            position={position as [number, number, number]}
            rotation={rotation as [number, number, number]}
            onClick={(e) => { e.stopPropagation(); onClick(); }}
        >
            <meshStandardMaterial color={color} />
        </mesh>
    );
};

/**
 * Ramp Preview Component
 */
export const RampPreview: React.FC<{
    startPoint: THREE.Vector3;
    endPoint: THREE.Vector3;
    width: number;
    slope: number;
}> = ({ startPoint, endPoint, width, slope }) => {
    const preview = useMemo(() => {
        const length = new THREE.Vector2(endPoint.x - startPoint.x, endPoint.y - startPoint.y).length();
        const height = length / slope;

        const direction = new THREE.Vector3().subVectors(endPoint, startPoint).normalize();
        const angle = Math.atan2(direction.y, direction.x);

        const center = startPoint.clone()
            .add(direction.clone().multiplyScalar(length / 2))
            .add(new THREE.Vector3(0, 0, height / 2));

        return (
            <group position={center} rotation={[0, 0, angle]}>
                <mesh>
                    <boxGeometry args={[length, width, height]} />
                    <meshBasicMaterial color="#00ff00" transparent opacity={0.3} />
                </mesh>
                <Line
                    points={[
                        [-length / 2, -width / 2, -height / 2], [length / 2, -width / 2, height / 2],
                        [length / 2, width / 2, height / 2], [-length / 2, width / 2, -height / 2],
                        [-length / 2, -width / 2, -height / 2]
                    ]}
                    color="#00ff00"
                />
            </group>
        );
    }, [startPoint, endPoint, width, slope]);

    return preview;
};

// ============================================================================
// COMPONENT TOOL
// ============================================================================

export interface ComponentData {
    position: number[];
    rotation: number;
    familyType: string;
    parameters: Record<string, any>;
}

/**
 * Component Tool Hook - Place Generic Components
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useComponentTool() {
    const [previewPosition, setPreviewPosition] = useState<THREE.Vector3 | null>(null);
    const [rotation, setRotation] = useState(0);
    const [familyType, setFamilyType] = useState('Standard');

    const addEntity = useAppStore((s) => s.addEntity);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const { raycaster, pointer, camera, gl } = useThree();

    const isActive = activeTool === 'component';

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    const snap = useCallback((point: THREE.Vector3): THREE.Vector3 => {
        const gs = 0.1;
        return applyOsnap(snapToGroundGrid(point, gs), camera, gl);
    }, [camera, gl]);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        const point = getGroundPoint();
        if (!point) return;
        const snapped = snap(point);

        addEntity({
            id: generateId(),
            type: 'component',
            name: `Component-${Date.now()}`,
            visible: true,
            locked: false,
            layer: activeLayerId,
            data: {
                position: [snapped.x, snapped.y, snapped.z],
                rotation,
                familyType,
                parameters: {},
            } as ComponentData,
        });
    }, [isActive, rotation, familyType, getGroundPoint, snap, addEntity, activeLayerId]);

    const handlePointerMove = useCallback(() => {
        if (!isActive) return;

        const point = getGroundPoint();
        if (point) {
            // Grid-snapped dedupe: repeat cell reads keep the previous
            // reference so React bails out of the tool-scope re-render.
            setPreviewPosition((prev) => {
                const snapped = snap(point);
                return prev && prev.distanceToSquared(snapped) < 1e-6 ? prev : snapped;
            });
        }
    }, [isActive, getGroundPoint, snap]);

    // Keyboard handlers
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isActive) return;

            if (e.key === ' ') { // Spacebar to rotate
                e.preventDefault();
                setRotation(r => (r + Math.PI / 2) % (Math.PI * 2));
            } else if (e.key === 'Escape') {
                setPreviewPosition(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isActive]);

    return {
        isActive,
        previewPosition,
        rotation,
        familyType,
        setRotation,
        setFamilyType,
        handlePointerDown,
        handlePointerMove,
    };
}

/**
 * Component Mesh Component
 */
export const ComponentMesh: React.FC<{
    data: ComponentData;
    isSelected: boolean;
    isHovered: boolean;
    onClick: () => void;
}> = ({ data, isSelected, isHovered, onClick }) => {
    const color = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : '#a0a0ff';

    // Minimap schematic: a neutral marker box at the component's real
    // parameter dimensions (the main viewport renders the family geometry).
    const width = Number(data.parameters?.['Width']) || 0.5;
    const depth = Number(data.parameters?.['Depth']) || 0.5;
    const height = Number(data.parameters?.['Height']) || 0.5;

    return (
        <group
            position={new THREE.Vector3(...data.position)}
            rotation={[0, 0, data.rotation]}
            onClick={(e) => { e.stopPropagation(); onClick(); }}
        >
            <mesh position={[0, 0, height / 2]}>
                <boxGeometry args={[width, depth, height]} />
                <meshStandardMaterial color={color} />
            </mesh>
        </group>
    );
};

/**
 * Component Preview Component
 */
export const ComponentPreview: React.FC<{
    position: THREE.Vector3;
    rotation: number;
}> = ({ position, rotation }) => {
    return (
        <group position={position} rotation={[0, 0, rotation]}>
            {/* Preview wireframe box (Z-up: the object height axis is Z) */}
            <mesh position={[0, 0, 0.5]}>
                <boxGeometry args={[0.5, 0.5, 1.0]} />
                <meshBasicMaterial color="#00ff00" wireframe />
            </mesh>
            {/* Direction indicator (front faces +Y locally) */}
            <Line
                points={[[0, 0, 0], [0, 0.5, 0]]}
                color="#00ff00"
            />
        </group>
    );
};

// ============================================================================
// AREA TOOL
// ============================================================================

export interface AreaData {
    position: number[];
    levelId: string;
    boundary?: number[][]; // Optional boundary points
}

/**
 * Area Tool Hook - Place Area Markers
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAreaTool() {
    const [previewPosition, setPreviewPosition] = useState<THREE.Vector3 | null>(null);

    const addEntity = useAppStore((s) => s.addEntity);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const { raycaster, pointer, camera, gl } = useThree();

    const isActive = activeTool === 'area';

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    const snap = useCallback((point: THREE.Vector3): THREE.Vector3 => {
        const gs = 0.5; // Snap to 0.5m for areas
        return applyOsnap(snapToGroundGrid(point, gs), camera, gl);
    }, [camera, gl]);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        const point = getGroundPoint();
        if (!point) return;
        const snapped = snap(point);

        addEntity({
            id: generateId(),
            type: 'area',
            name: `Area-${Date.now()}`,
            visible: true,
            locked: false,
            layer: activeLayerId,
            data: {
                position: [snapped.x, snapped.y, snapped.z],
                levelId: 'Level 1',
            } as AreaData,
        });
    }, [isActive, getGroundPoint, snap, addEntity, activeLayerId]);

    const handlePointerMove = useCallback(() => {
        if (!isActive) return;

        const point = getGroundPoint();
        if (point) {
            // Grid-snapped dedupe: repeat cell reads keep the previous
            // reference so React bails out of the tool-scope re-render.
            setPreviewPosition((prev) => {
                const snapped = snap(point);
                return prev && prev.distanceToSquared(snapped) < 1e-6 ? prev : snapped;
            });
        }
    }, [isActive, getGroundPoint, snap]);

    // Keyboard handlers
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isActive) return;

            if (e.key === 'Escape') {
                setPreviewPosition(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isActive]);

    return {
        isActive,
        previewPosition,
        handlePointerDown,
        handlePointerMove,
    };
}

/**
 * Area Mesh Component
 */
export const AreaMesh: React.FC<{
    data: AreaData;
    isSelected: boolean;
    isHovered: boolean;
    onClick: () => void;
}> = ({ data, isSelected, isHovered, onClick }) => {
    const color = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : '#ffff00';

    return (
        <group
            position={new THREE.Vector3(...data.position)}
            onClick={(e) => { e.stopPropagation(); onClick(); }}
        >
            {/* Area Marker (X shape, in the XY ground plane) */}
            <Line
                points={[[-0.5, -0.5, 0], [0.5, 0.5, 0]]}
                color={color}
                lineWidth={2}
            />
            <Line
                points={[[-0.5, 0.5, 0], [0.5, -0.5, 0]]}
                color={color}
                lineWidth={2}
            />
            {/* Label Placeholder */}
            {/* In a real app, this would be a Text component showing Area name and value */}
        </group>
    );
};

/**
 * Area Preview Component
 */
export const AreaPreview: React.FC<{
    position: THREE.Vector3;
}> = ({ position }) => {
    return (
        <group position={position}>
            <Line
                points={[[-0.5, -0.5, 0], [0.5, 0.5, 0]]}
                color="#00ff00"
                lineWidth={2}
            />
            <Line
                points={[[-0.5, 0.5, 0], [0.5, -0.5, 0]]}
                color="#00ff00"
                lineWidth={2}
            />
        </group>
    );
};

// ============================================================================
// TOPOSOLID TOOL
// ============================================================================

export interface ToposolidData {
    boundary: number[][]; // 2D points [x, y] (ground plane, Z-up)
    height: number;
    baseElevation: number;
}

/**
 * Toposolid Tool Hook - Sketch Boundary
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useToposolidTool() {
    const [points, setPoints] = useState<THREE.Vector3[]>([]);
    const [previewPoint, setPreviewPoint] = useState<THREE.Vector3 | null>(null);
    const [height, setHeight] = useState(1.0); // Default depth

    const addEntity = useAppStore((s) => s.addEntity);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const { raycaster, pointer, camera, gl } = useThree();

    const isActive = activeTool === 'toposolid';

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    const snap = useCallback((point: THREE.Vector3): THREE.Vector3 => {
        const gs = 0.5;
        return applyOsnap(snapToGroundGrid(point, gs), camera, gl);
    }, [camera, gl]);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        const point = getGroundPoint();
        if (!point) return;
        const snapped = snap(point);

        setPoints(prev => [...prev, snapped]);
    }, [isActive, getGroundPoint, snap]);

    const handlePointerMove = useCallback(() => {
        if (!isActive) return;

        const point = getGroundPoint();
        if (point) {
            // Grid-snapped dedupe: repeat cell reads keep the previous
            // reference so React bails out of the tool-scope re-render.
            setPreviewPoint((prev) => {
                const snapped = snap(point);
                return prev && prev.distanceToSquared(snapped) < 1e-6 ? prev : snapped;
            });
        }
    }, [isActive, getGroundPoint, snap]);

    const handleDoubleClick = useCallback(() => {
        if (!isActive || points.length < 3) return;

        // Create Toposolid
        const boundary = points.map(p => [p.x, p.y]);
        const baseElevation = points[0].z; // Assume flat for now, or take min/max

        addEntity({
            id: generateId(),
            type: 'toposolid',
            name: `Toposolid-${Date.now()}`,
            visible: true,
            locked: false,
            layer: activeLayerId,
            data: {
                boundary,
                height,
                baseElevation,
            } as ToposolidData,
        });

        setPoints([]);
        setPreviewPoint(null);
    }, [isActive, points, height, addEntity, activeLayerId]);

    // Keyboard handlers
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isActive) return;

            if (e.key === 'Enter') {
                handleDoubleClick();
            } else if (e.key === 'Escape') {
                setPoints([]);
                setPreviewPoint(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isActive, handleDoubleClick]);

    return {
        isActive,
        points,
        previewPoint,
        height,
        setHeight,
        handlePointerDown,
        handlePointerMove,
        handleDoubleClick,
    };
}

/**
 * Toposolid Mesh Component
 */
export const ToposolidMesh: React.FC<{
    data: ToposolidData;
    isSelected: boolean;
    isHovered: boolean;
    onClick: () => void;
}> = ({ data, isSelected, isHovered, onClick }) => {
    const color = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : '#4caf50'; // Green for grass

    const geometry = useMemo(() => {
        if (data.boundary.length < 3) return null;

        const shape = new THREE.Shape();
        const first = data.boundary[0];
        shape.moveTo(first[0], first[1]);

        for (let i = 1; i < data.boundary.length; i++) {
            shape.lineTo(data.boundary[i][0], data.boundary[i][1]);
        }
        shape.closePath();

        return new THREE.ExtrudeGeometry(shape, {
            depth: data.height,
            bevelEnabled: false,
        });
    }, [data.boundary, data.height]);

    if (!geometry) return null;

    return (
        <mesh
            geometry={geometry}
            position={[0, 0, data.baseElevation]}
            onClick={(e) => { e.stopPropagation(); onClick(); }}
        >
            <meshStandardMaterial color={color} side={THREE.DoubleSide} defines={BACKFACE_DEFINES} />
        </mesh>
    );
};

/**
 * Toposolid Preview Component
 */
export const ToposolidPreview: React.FC<{
    points: THREE.Vector3[];
    previewPoint: THREE.Vector3 | null;
}> = ({ points, previewPoint }) => {
    const allPoints = useMemo(() => {
        const pts = [...points];
        if (previewPoint) pts.push(previewPoint);
        return pts;
    }, [points, previewPoint]);

    if (allPoints.length < 2) return null;

    return (
        <>
            <Line points={allPoints} color="#4caf50" lineWidth={2} />
            {allPoints.map((v, i) => (
                <mesh key={i} position={v}>
                    <sphereGeometry args={[0.1, 8, 8]} />
                    <meshBasicMaterial color="#4caf50" />
                </mesh>
            ))}
            {/* Close loop preview if enough points */}
            {allPoints.length >= 3 && (
                <Line points={[allPoints[allPoints.length - 1], allPoints[0]]} color="#4caf50" lineWidth={1} dashed />
            )}
        </>
    );
};
